import { FeatureTile, FeaturesGridV2Props } from "@/shared/data/home.v2.data";
import {
  faBrain,
  faGlobe,
  faInfinity,
  faLink,
  faMegaphone,
  faMobileScreen,
  faMoneyBill1,
  faShield,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import Link from "next/link";
import SectionHeader from "./shared/SectionHeader";
import Text from "./shared/Text";

/**
 * Tile id → FontAwesome Pro Regular IconDefinition. We render icons via
 * the `<FontAwesomeIcon>` component (not raw `<i>` class strings)
 * because `_app.tsx` sets `config.autoAddCss = false` — meaning only
 * the utility CSS ships, not the glyph CSS, so class-name-only icons
 * would never render. Keyed by tile title to keep the data file free
 * of direct FontAwesome imports.
 */
const iconByTitle: Record<string, IconDefinition> = {
  "AI Website Builder": faGlobe,
  "AI Agent": faInfinity,
  "Mobile App": faMobileScreen,
  "Integrated Payments": faMoneyBill1,
  "AI Brain": faBrain,
  "Marketing Tools": faMegaphone,
  Security: faShield,
  "Third-Party Support": faLink,
};

/**
 * FeaturesGridV2 — "FEATURES / Features That Level Up Your Business"
 *
 * Light bg, SectionHeader (light-magenta) followed by a two-row marquee:
 *  • Top row scrolls left → right
 *  • Bottom row scrolls right → left
 *
 * Cards are wide enough (≥280px mobile, ≥360px desktop) that a single
 * row is comfortably longer than any realistic viewport, so the
 * duplicated-track seam never shows the same card twice simultaneously.
 * Hover pauses each row so visitors can read a tile.
 *
 * Marquee CSS lives in `src/styles/globals.css` (search: feature-marquee-*)
 * because styled-jsx can't style elements rendered by a child component.
 */

const FeatureCard = ({ tile }: { tile: FeatureTile }) => {
  const iconDef = iconByTitle[tile.title];
  const cardClassName =
    "feature-marquee-card shrink-0 rounded-2xl border border-slateStroke bg-white p-6 transition-shadow duration-200 md:p-7" +
    (tile.href ? " hover:border-brandPrimary/30 hover:shadow-md" : "");
  const body = (
    <>
      {/* Icon — coloured outline glyph (FontAwesome Pro Regular) with
          no pill background, matching the reference design. The colour
          is per-tile so each feature gets its own accent. */}
      <div className="mb-6">
        {iconDef ? (
          <FontAwesomeIcon
            icon={iconDef}
            style={{
              color: tile.iconColor ?? "#5766F2",
              fontSize: 28,
              lineHeight: 1,
            }}
            aria-hidden="true"
          />
        ) : null}
      </div>
      {/* Heading: Inter Medium (500). `title-sm` ships font-semibold +
          tracking-[-1.5px] which read too heavy on these short card
          titles ("AI Agent", "Security"). We drop to Medium with normal
          tracking so the titles sit in the same weight rhythm as the
          rest of HomeV2 (SectionHeader, HeroV2). */}
      <h3
        className="block w-full font-inter text-[18px] font-medium leading-tight text-neutralBlack"
        style={{ letterSpacing: 0 }}
      >
        {tile.title}
      </h3>
      <Text
        as="p"
        variant="body-md"
        className="mt-2 block w-full text-darkSlate04"
        style={{ letterSpacing: 0 }}
      >
        {tile.description}
      </Text>
    </>
  );

  if (tile.href) {
    return (
      <Link href={tile.href} className={cardClassName} aria-label={tile.title}>
        {body}
      </Link>
    );
  }
  return <div className={cardClassName}>{body}</div>;
};

type RowProps = {
  tiles: FeatureTile[];
  direction: "left" | "right";
};

const FeatureMarqueeRow = ({ tiles, direction }: RowProps) => {
  // Duplicate the tile set so the translate loop reads as seamless. The
  // track width is 2× the unique content width; a half-translate cycles
  // one complete set in/out.
  const loop = [...tiles, ...tiles];
  return (
    // `.feature-marquee-row` handles the clip + vertical overflow in
    // globals.css. We deliberately don't use `overflow-hidden` here,
    // otherwise the hover lift + shadow on cards would get cropped at
    // the row's top/bottom edge.
    <div className="feature-marquee-row relative w-full">
      <div
        className={
          direction === "left"
            ? "feature-marquee-track-left flex"
            : "feature-marquee-track-right flex"
        }
        style={{ width: "max-content" }}
      >
        {loop.map((tile, i) => (
          <FeatureCard key={`${tile.title}-${i}`} tile={tile} />
        ))}
      </div>
    </div>
  );
};

const FeaturesGridV2 = ({
  eyebrow,
  title,
  highlightedWord,
  subtitle,
  tiles,
}: FeaturesGridV2Props) => {
  // Split the title around the highlighted phrase so SectionHeader can
  // render the muted + bright halves. Falls back to single title otherwise.
  let titleMuted: string | undefined;
  let titleBright: string | undefined;
  if (highlightedWord && title.includes(highlightedWord)) {
    const idx = title.indexOf(highlightedWord);
    titleMuted = title.slice(0, idx).trim();
    titleBright = title.slice(idx).trim();
  }

  // Split tiles into two rows. Odd totals bias the top row with the
  // extra card so the grid doesn't look lopsided on the bottom.
  const mid = Math.ceil(tiles.length / 2);
  const topRow = tiles.slice(0, mid);
  const bottomRow = tiles.slice(mid);

  return (
    <section className="w-full bg-slateLight">
      {/* Header stays inside the container for horizontal rhythm with
          the rest of the page. */}
      <div className="container mx-auto px-4 pb-0 pt-10 sm:px-6 md:px-8 md:pt-20 lg:px-12 lg:pt-24">
        <div className="mx-auto max-w-6xl">
        <SectionHeader
          eyebrow={eyebrow}
          titleMuted={titleMuted}
          titleBright={titleBright}
          title={titleMuted ? undefined : title}
          subtitle={subtitle}
          variant="light-magenta"
          align="center"
        />
      </div>
      </div>

      {/* Two-row marquee lives OUTSIDE the container so the cards run
          edge-to-edge with the viewport. The container's horizontal
          padding (px-4 → lg:px-12) would otherwise leave a strip on
          each side where the marquee couldn't reach. Bottom padding
          restores the section's vertical rhythm below the marquee. */}
      <div className="feature-marquee-shell mt-12 w-full pb-10 md:mt-16 md:pb-20 lg:pb-24">
        <FeatureMarqueeRow tiles={topRow} direction="right" />
        <FeatureMarqueeRow tiles={bottomRow} direction="left" />
      </div>
    </section>
  );
};

export default FeaturesGridV2;
